"use client";

import {
  Bot,
  CalendarDays,
  Headphones,
  MessageCircle,
  Rocket,
  Search,
} from "lucide-react";

import BoxReveal from "@cohub42/ui/components/magicui/box-reveal";
import WordPullUp from "@cohub42/ui/components/magicui/word-pull-up";
import { BackgroundBeams } from "@cohub42/ui/components/ui/background-beams";

const platformFeatures = [
  {
    icon: <Search className="h-6 w-6" />,
    title: "AI-Powered Matching",
    description:
      "Swipe through AI-curated recommendations filtered by role, skills, and location.",
    gradient: "from-primary to-primary/20",
  },
  {
    icon: <CalendarDays className="h-6 w-6" />,
    title: "Meetups & Events",
    description:
      "Discover, host, and join entrepreneur meetups and networking events near you.",
    gradient: "from-primary to-primary/20",
  },
  {
    icon: <Rocket className="h-6 w-6" />,
    title: "Launch Hub",
    description:
      "Access accelerators, incubators, mentorship programs, and startup resources.",
    gradient: "from-primary to-primary/20",
  },
  {
    icon: <MessageCircle className="h-6 w-6" />,
    title: "Real-Time Chat",
    description:
      "Connect instantly with matched co-founders through private and group messaging.",
    gradient: "from-primary to-primary/20",
  },
  {
    icon: <Bot className="h-6 w-6" />,
    title: "AI Assistant",
    description:
      "Get strategic startup guidance from an AI-powered mentor available 24/7.",
    gradient: "from-primary to-primary/20",
  },
  {
    icon: <Headphones className="h-6 w-6" />,
    title: "News & Podcasts",
    description:
      "Stay informed with curated startup news, blogs, and entrepreneur podcasts.",
    gradient: "from-primary to-primary/20",
  },
];

export default function CoFounderFeaturesSection() {
  return (
    <section className="relative py-20 sm:py-28">
      <div className="absolute inset-0 overflow-hidden">
        <BackgroundBeams className="opacity-20" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <BoxReveal boxColor={"transparent"} duration={0.5} width="100%">
            <div className="mb-4 inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary ring-1 ring-primary/20">
              🎯 Platform Features
            </div>
          </BoxReveal>

          <WordPullUp
            className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl"
            words="Everything to Find Your Co-Founder"
          />

          <BoxReveal boxColor={"transparent"} duration={0.5} width="100%">
            <p className="mt-4 text-base leading-7 text-gray-600 dark:text-gray-300">
              AI-powered tools to match, connect, and build with your ideal
              partner.
            </p>
          </BoxReveal>
        </div>

        <div className="mx-auto mt-12 max-w-5xl sm:mt-16">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {platformFeatures.map((feature, index) => (
              <BoxReveal
                key={feature.title}
                boxColor={"transparent"}
                duration={0.4 + index * 0.08}
                width="100%"
              >
                <div className="group relative overflow-hidden rounded-xl bg-white p-5 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-lg dark:bg-gray-800/60">
                  <div className="flex items-start gap-4">
                    <div
                      className={`inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${feature.gradient} text-white shadow-md`}
                    >
                      {feature.icon}
                    </div>

                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                        {feature.title}
                      </h3>
                      <p className="mt-1 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              </BoxReveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
